import type { MediaDetails, MediaEvent, MediaRequest, Trip, Vehicle } from '@cobliteam/common-types';
import { endOfDay, startOfDay } from '@cobliteam/utils/date';

// Types
interface TimeMarker {
  time: number;
  percent: number;
  isMajor: boolean;
}

interface ViewWindow {
  start: number;
  end: number;
}

interface PeriodOption {
  id: string;
  label: string;
  value: string;
  isDisabled: boolean;
  trip: Trip;
  overnightTripStartTimestamp?: number;
}

interface VehicleGroupOption {
  value: string;
  label: string;
  vehicles: Vehicle[];
}

// Constants
const LEGEND_ITEMS = [
  { id: 'no-activity', icon: 'square', className: 'fill-gray-4 text-gray-4', label: 'Sem atividade' },
  { id: 'recent', icon: 'circle', className: 'fill-primary-6 text-primary-6', label: 'Recente' },
  { id: 'next-to-expire', icon: 'circle', className: 'fill-accessory-3 text-accessory-3', label: 'Próximo a vencer' },
  { id: 'expired', icon: 'circle', className: 'fill-gray text-gray', label: 'Gravação Expirada' },
];

const STEPS = [
  {
    id: 'vehicle-and-date',
    title: 'Veículo e data',
  },
  {
    id: 'media-type',
    title: 'Categoria do vídeo',
  },
  {
    id: 'video-selection',
    title: 'Seleção do vídeo',
  },
  {
    id: 'request-confirmation',
    title: 'Confirmação de solicitação',
  },
];

// Helper functions
const getTripsKey = (trips: Trip[] | undefined): string => {
  if (!trips?.length) return '';

  return trips.map((trip) => `${trip.startTimestamp}-${trip.endTimestamp}-${trip.isFootageAvailable}`).join('|');
};

const normalizeTripsData = (trips: Trip[]): Trip[] => {
  return trips.flatMap((trip) => {
    const tripStart = startOfDay(new Date(trip.startTimestamp));
    const tripEnd = startOfDay(new Date(trip.endTimestamp));

    if (tripEnd > tripStart) {
      return [
        {
          ...trip,
          endTimestamp: endOfDay(tripStart).getTime(),
        },
        {
          ...trip,
          startTimestamp: tripEnd.getTime(),
        },
      ];
    }
    return [trip];
  });
};

const createDateAvailabilityData = (trips: Trip[]): Trip[] => {
  return trips.flatMap((trip) => {
    const tripStart = startOfDay(new Date(trip.startTimestamp));
    const tripEnd = startOfDay(new Date(trip.endTimestamp));

    if (tripEnd > tripStart) {
      // Create entries for all days in the trip range for calendar availability
      const dateEntries: Trip[] = [];
      const currentDate = new Date(tripStart);

      while (currentDate <= tripEnd) {
        dateEntries.push({
          ...trip,
          startTimestamp: currentDate.getTime(),
          endTimestamp: endOfDay(currentDate).getTime(),
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }

      return dateEntries;
    }
    return [trip];
  });
};

const createPeriodSelectionData = (trips: Trip[]): Trip[] => {
  return trips.flatMap((trip) => {
    const tripStart = startOfDay(new Date(trip.startTimestamp));
    const tripEnd = startOfDay(new Date(trip.endTimestamp));

    if (tripEnd > tripStart) {
      // For period selection, we need to create entries for each day
      // but preserve the original trip time ranges within each day
      const periodEntries: Trip[] = [];
      const currentDate = new Date(tripStart);

      while (currentDate <= tripEnd) {
        const dayStart = currentDate.getTime();
        const dayEnd = endOfDay(currentDate).getTime();

        // Calculate the intersection of the trip with this specific day
        const tripStartTime = Math.max(trip.startTimestamp, dayStart);
        const tripEndTime = Math.min(trip.endTimestamp, dayEnd);

        // Only create an entry if there's an intersection
        if (tripStartTime < tripEndTime) {
          periodEntries.push({
            ...trip,
            startTimestamp: tripStartTime,
            endTimestamp: tripEndTime,
          });
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }

      return periodEntries;
    }
    return [trip];
  });
};

// Type guards to verify media details typings
const isMediaEvent = (details: MediaDetails): details is MediaEvent => 'eventTrigger' in details;
const isMediaFootage = (details: MediaDetails): details is MediaRequest => 'duration' in details;

export { LEGEND_ITEMS, STEPS };
export { getTripsKey, normalizeTripsData, createDateAvailabilityData, createPeriodSelectionData, isMediaEvent, isMediaFootage };
export type { PeriodOption, VehicleGroupOption };
